import { Button, Modal, Stack, Text, Textarea, Title } from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import type { WorkOrderId } from "../../../lib/workIds";
import { displayWorkOrderNumber } from "./presentation";
import { WorkOrderTimeAndCostsSection } from "./WorkOrderTimeEntriesSection";

type CompletableWorkOrder = {
  completionNote?: string | null;
  id: WorkOrderId;
  number: string;
  title: string;
  totalCost: number;
};

type CompleteWorkOrderModalProps = {
  open: boolean;
  workOrder: CompletableWorkOrder;
  onOpenChange: (open: boolean) => void;
  onComplete: (input: { completionNotes: string }) => Promise<void>;
  canEditWorkOrder: boolean;
  canAddTimeForCompletionReview?: boolean;
  hasPendingTimeChanges?: boolean;
};

type Step = "form" | "success";

export function CompleteWorkOrderModal({
  open,
  workOrder,
  onOpenChange,
  onComplete,
  canEditWorkOrder,
  canAddTimeForCompletionReview = false,
  hasPendingTimeChanges: hasPendingTabTimeChanges = false,
}: CompleteWorkOrderModalProps) {
  const [step, setStep] = useState<Step>("form");
  const [completionNotes, setCompletionNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPendingReviewTimeChanges, setHasPendingReviewTimeChanges] =
    useState(false);
  const [isTimeReviewReady, setIsTimeReviewReady] = useState(false);
  const hasPendingTimeChanges =
    hasPendingTabTimeChanges || hasPendingReviewTimeChanges;
  const [error, setError] = useState<string | null>(null);
  // Read only when the modal opens: a background refresh while the user is
  // typing must not overwrite what they have entered.
  const savedNote = useRef(workOrder.completionNote);
  savedNote.current = workOrder.completionNote;

  useEffect(() => {
    if (open) {
      setStep("form");
      // A Work Order that was reopened keeps its earlier completion note, so
      // it is offered again for the user to keep or edit.
      setCompletionNotes(savedNote.current ?? "");
      setError(null);
      setIsSubmitting(false);
      setHasPendingReviewTimeChanges(false);
      setIsTimeReviewReady(false);
    }
  }, [open]);

  function downloadCloseoutReport() {
    const report = [
      `Work Order: ${displayWorkOrderNumber(workOrder.number)}`,
      `Title: ${workOrder.title}`,
      `Completion notes: ${completionNotes}`,
      `Cost: $${workOrder.totalCost.toFixed(2)}`,
    ].join("\n");
    const url = URL.createObjectURL(new Blob([report], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${displayWorkOrderNumber(workOrder.number)}-closeout.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function complete() {
    setIsSubmitting(true);
    setError(null);
    try {
      await onComplete({ completionNotes: completionNotes.trim() });
      setStep("success");
    } catch (caught) {
      // Deliberately not clearing completionNotes here: a rejected completion
      // attempt must leave whatever the user already entered in place.
      setError(
        caught instanceof Error
          ? caught.message
          : "Unable to complete Work Order.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      dismissible={!hasPendingReviewTimeChanges}
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={
        step === "form"
          ? `Complete ${displayWorkOrderNumber(workOrder.number)}`
          : "Work order complete"
      }
    >
      {step === "form" ? (
        <Stack gap={16}>
          <Text tone="subtle">
            Review time before closing out this work order. Add any final time
            entries and notes, then complete it.
          </Text>
          <WorkOrderTimeAndCostsSection
            canEditWorkOrder={canEditWorkOrder}
            canAddTimeForCompletionReview={canAddTimeForCompletionReview}
            completionReview
            enabled={open && step === "form"}
            onPendingChange={setHasPendingReviewTimeChanges}
            onReviewReadyChange={setIsTimeReviewReady}
            workOrderId={workOrder.id}
          />
          {hasPendingTimeChanges ? (
            <Text tone="subtle">
              Save or cancel the time entry before completing this Work Order.
            </Text>
          ) : null}
          <Textarea
            aria-label="Completion notes"
            label="Completion notes (required)"
            onChange={(event) => setCompletionNotes(event.target.value)}
            required
            value={completionNotes}
          />
          {error ? <Text tone="negative">{error}</Text> : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button
              disabled={hasPendingReviewTimeChanges}
              onClick={() => onOpenChange(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={
                isSubmitting ||
                hasPendingTimeChanges ||
                !isTimeReviewReady ||
                !completionNotes.trim()
              }
              onClick={complete}
            >
              {isSubmitting ? "Saving…" : "Done"}
            </Button>
          </div>
        </Stack>
      ) : (
        <Stack gap={16}>
          <div className="flex flex-col items-center gap-(--spacing-12) py-(--spacing-20) text-center">
            <div className="flex size-(--spacing-56) items-center justify-center rounded-full bg-primary-container text-primary text-(length:--font-size-title-lg)">
              ✓
            </div>
            <Title>{displayWorkOrderNumber(workOrder.number)} closed</Title>
            <Text tone="subtle">
              Your completion notes were recorded and the Work Order is now
              completed.
            </Text>
          </div>
          <div className="flex justify-end gap-(--spacing-8) border-t border-outline-subtle pt-(--spacing-16)">
            <Button onClick={downloadCloseoutReport} variant="outline">
              Download closeout report
            </Button>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </Stack>
      )}
    </Modal>
  );
}
